import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormDateInput from '../../../components/form/FormDateInput';
import FormSelect from '../../../components/form/FormSelect';
import { useDispatch } from 'react-redux';
import {
  archiveOptionsGet,
  didDumpReportGet
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import { endOfDay, startOfDay } from 'date-fns';
import AdminHeader from '../../../components/header/AdminHeader';
import Pagination from '../../../components/form/Pagination';
import CallexLoader from '../../../components/CallexLoader';
import * as Icon from 'react-feather';
import FormInput from '../../../components/form/FormInput';
import {
  checkModulePermission,
  fileDownload,
  formatArchiveOptions,
  formatPayloadDate,
  forwardingTypeOptions,
  getDateRangeFromLabel
} from '../../../utils/common';
import {
  audioPromptsAllOptionGet,
  didOptionGet,
  didProcessOptionGet,
  didUserOptionGet,
  ivrOptionsGet,
  queuesOptionGet
} from '../../../store/slices/tenantSlices/incomingSlice';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import AudioPlayerModal from '../../../components/Modals/AudioPlayerModal';
import config from '../../../config';
import { useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';
import { callexAiAgentOptionsGet } from '../../../store/slices/tenantSlices/configurationSlice';

const DidDumpReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const userRolePermission = useUserRolePermission();

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  // Starts true because the first search waits for the archive options to resolve -
  // without this the empty state flashes before the loader appears.
  const [isLoading, setIsLoading] = useState(true);

  const [processOptions, setProcessOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [didOptions, setDidOptions] = useState([]);
  const [ivrOption, setIvrOption] = useState([]);
  const [queuesOption, setQueuesOption] = useState([]);
  const [audioPromptOption, setAudioPromptOption] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [csvReportData, setCsvReportData] = useState([]);
  const [csvHeaderData, setCsvHeaderData] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isCsvDownload, setIsCsvDownload] = useState(false);
  const [audioDetails, setAudioDetails] = useState(null);
  const [archiveOptions, setArchiveOptions] = useState([]);
  const hasAutoSearchedRef = useRef(false);
  const [aiPhoneNumberOption, setAiPhoneNumberOption] = useState([]);

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    page: 1,
    limit: 10,
    forwarding_type: '',
    forwarding_options: '',
    did_number: '',
    start_date: startOfDay(today),
    end_date: endOfDay(today),
    archiveId: 0
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required'),
    forwarding_type: Yup.string().required('forwarding type is required')
  });

  // `range` lets the first automatic search use the month range as soon as it is
  // derived, without waiting for setFieldValue to land in `values`.
  const fetchReport = async (range) => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(range?.start_date || values.start_date);
      const formattedEndDate = formatPayloadDate(range?.end_date || values.end_date);
      const payload = {
        ...values,
        did_number: values?.did_number || undefined,
        forwarding_type: values?.forwarding_type || undefined,
        forwarding_options: values?.forwarding_options || undefined
      };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;

      const response = await dispatch(didDumpReportGet(payload)).unwrap();
      if (response) {
        setReportData(response);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const onSubmit = () => fetchReport();

  // Runs the load-time search exactly once, whichever path resolves the range first.
  const runInitialSearch = (range) => {
    if (hasAutoSearchedRef.current) return;
    hasAutoSearchedRef.current = true;
    fetchReport(range);
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  const forwardingSelectOptions = [
    'transfer_to_process',
    'transfer_to_queue',
    'transfer_to_playback',
    'transfer_to_agent',
    'transfer_to_ivr',
    'transfer_to_ai_number'
  ];

  const getForwardingOptions = (forwarding_type) => {
    if (forwarding_type === 'transfer_to_process') {
      return processOptions;
    }
    if (forwarding_type === 'transfer_to_queue') {
      return queuesOption;
    }
    if (forwarding_type === 'transfer_to_playback') {
      return audioPromptOption;
    }
    if (forwarding_type === 'transfer_to_agent') {
      return userOptions;
    }
    if (forwarding_type === 'transfer_to_ivr') {
      return ivrOption;
    }
    if (forwarding_type === 'transfer_to_ai_number') {
      return aiPhoneNumberOption;
    }
    return [];
  };

  const getForwardingLabel = (forwarding_type) => {
    if (forwarding_type === 'transfer_to_process') {
      return 'Process';
    }
    if (forwarding_type === 'transfer_to_queue') {
      return 'Queue';
    }
    if (forwarding_type === 'transfer_to_playback') {
      return 'Playback';
    }
    if (forwarding_type === 'transfer_to_agent') {
      return 'User';
    }
    if (forwarding_type === 'transfer_to_ivr') {
      return 'IVR';
    }
    if (forwarding_type === 'transfer_to_ai_number') {
      return 'AI Agent';
    }
    return '';
  };

  useEffect(() => {
    // Skip the mount pass - the initial search is fired once the archive-derived
    // range is known, so it matches the dates shown in the filter. Page/limit
    // changes after that keep paginating as before.
    if (!hasAutoSearchedRef.current) return;
    onSubmit();
  }, [values?.limit, values?.page]);

  const getAudioPromptOption = async () => {
    try {
      const response = await dispatch(audioPromptsAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setAudioPromptOption(options);
      } else {
        setAudioPromptOption([]);
      }
    } catch (error) {
      setAudioPromptOption([]);
      console.log('error', error);
    }
  };

  const getIvrOption = async () => {
    try {
      const response = await dispatch(ivrOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setIvrOption(options);
      } else {
        setIvrOption([]);
      }
    } catch (error) {
      setIvrOption([]);
      console.log('error', error);
    }
  };

  const getAIAgentOption = async () => {
    try {
      const response = await dispatch(callexAiAgentOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id) || ""
        }));
        setAiPhoneNumberOption(options);
      } else {
        setAiPhoneNumberOption([]);
      }
    } catch (error) {
      setAiPhoneNumberOption([]);
      console.log('error', error);
    }
  };

  const getQueuesOption = async () => {
    try {
      const response = await dispatch(queuesOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setQueuesOption(options);
      } else {
        setQueuesOption([]);
      }
    } catch (error) {
      setQueuesOption([]);
      console.log('error', error);
    }
  };

  const getProcessOption = async () => {
    try {
      const response = await dispatch(didProcessOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getUserOption = async () => {
    try {
      const response = await dispatch(didUserOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.Username || '-',
          value: String(val.id)
        }));
        setUserOptions(options);
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      setUserOptions([]);
      console.log('error', error);
    }
  };

  const getDidOption = async () => {
    try {
      const response = await dispatch(didOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.number || '-',
          value: String(val.id)
        }));
        setDidOptions(options);
      } else {
        setDidOptions([]);
      }
    } catch (error) {
      setDidOptions([]);
      console.log('error', error);
    }
  };

  const getArchiveOptions = async () => {
    try {
      const response = await dispatch(archiveOptionsGet()).unwrap();
      if (response && response.data) {
        const options = formatArchiveOptions(response.data, timezone);
        setArchiveOptions(options);
      } else {
        setArchiveOptions([]);
        // No month options means no derived range, so fall back to the default dates.
        runInitialSearch();
      }
    } catch (error) {
      setArchiveOptions([]);
      runInitialSearch();
      console.log('error', error);
    }
  };

  useEffect(() => {
    getArchiveOptions();
    getDidOption();
    getIvrOption();
    getAudioPromptOption();
    getQueuesOption();
    getProcessOption();
    getUserOption();
    getAIAgentOption();
  }, []);

  const prepareExportProps = () => {
    const data = csvReportData || [];
    return { headers: csvHeaderData, data, filename: 'Daily_report.csv' };
  };

  const handleExportCSV = async () => {
    setIsExporting(true);

    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = {
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        did_number: values?.did_number || undefined,
        forwarding_type: values?.forwarding_type || undefined,
        forwarding_options: values?.forwarding_options || undefined,
        archiveId: values?.archiveId
      };

      const response = await dispatch(didDumpReportGet(payload)).unwrap();

      if (response) {
        const combinedHeaders = [...(response?.header_fields || [])];

        const headers = combinedHeaders.map((val) => ({ label: val?.label, key: val?.name }));

        setCsvReportData(response?.data || []);
        setCsvHeaderData(headers);
        setIsExporting(false);
        setIsCsvDownload(true);
      }
    } catch (error) {
      setIsExporting(false);
      console.log('Error exporting CSV:', error);
    }
  };

  const selectedOption = useMemo(
    () => archiveOptions.find((opt) => opt.value === values.archiveId),
    [values.archiveId, archiveOptions]
  );

  const selectedLabel = selectedOption?.label || null;

  const { minDate, maxDate } = useMemo(() => {
    return getDateRangeFromLabel(selectedLabel, timezone);
  }, [selectedLabel, timezone]);

  // The archive options land after mount, so the month range is only known here.
  // The first search has to wait for it, otherwise it fetches today's range while
  // the filter already shows the whole month.
  useEffect(() => {
    if (minDate && maxDate) {
      setFieldValue('start_date', minDate);
      setFieldValue('end_date', maxDate);
      runInitialSearch({ start_date: minDate, end_date: maxDate });
    } else if (archiveOptions?.length > 0) {
      // Options loaded but the label gave no range - do not leave the first search pending.
      runInitialSearch();
    }
  }, [minDate?.getTime(), maxDate?.getTime(), archiveOptions?.length]);

  return (
    <div>
      <AdminHeader
        title="DID Dump Report"
        isSearch
        isExportDisabled={
          !checkModulePermission('is_export', 'DID Dump Report', userRolePermission)
        }
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isExport={reportData?.data?.length > 0}
        csvProps={prepareExportProps()}
        onExportClick={() => handleExportCSV()}
        setIsCsvDownload={setIsCsvDownload}
        isCsvDownload={isCsvDownload}
        isExporting={isExporting}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Month"
                  placeholder="Select month"
                  value={values.archiveId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="archiveId"
                  options={archiveOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormDateInput
                  name="start_date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  minDate={minDate}
                  maxDate={values.end_date || maxDate}
                  value={values.start_date}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormDateInput
                  name="end_date"
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.start_date || minDate}
                  maxDate={maxDate}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  name="forwarding_type"
                  label="Forwarding Type"
                  placeholder="Select Forwarding Type"
                  value={values.forwarding_type}
                  options={forwardingTypeOptions}
                  onChange={(e) => {
                    handleChange(e);
                    if (e.target.value === 'transfer_to_hangup') {
                      setFieldValue('forwarding_options', 'Hangup');
                    } else {
                      setFieldValue('forwarding_options', '');
                    }
                  }}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              {values.forwarding_type !== '' ? (
                <Col lg="2" md="2" className="flex-row">
                  {forwardingSelectOptions.includes(values.forwarding_type) ? (
                    <FormSelect
                      name="forwarding_options"
                      label={getForwardingLabel(values.forwarding_type)}
                      placeholder={`Select ${getForwardingLabel(values.forwarding_type)}`}
                      value={values.forwarding_options}
                      options={getForwardingOptions(values.forwarding_type)}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                    />
                  ) : values.forwarding_type === 'transfer_to_call_forward' ? (
                    <FormInput
                      name="forwarding_options"
                      label="Call Forward (PSTN)"
                      placeholder="Enter Call Forward"
                      value={values.forwarding_options}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                    />
                  ) : values.forwarding_type === 'transfer_to_hangup' ? (
                    <FormInput
                      name="forwarding_options"
                      label="Hangup"
                      placeholder="Enter Hangup"
                      value={values.forwarding_options}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      disabled={true}
                    />
                  ) : null}
                </Col>
              ) : null}
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  name="did_number"
                  label="DID Number"
                  placeholder="Select DID Number"
                  value={values.did_number}
                  options={didOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.data?.length > 0 ? (
        <Card className="p-0 position-relative">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 369px)' : 'calc(100vh - 208px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {reportData?.header_fields?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {reportData?.header_fields?.map((value, idx) => {
                        if (value?.name === 'recording_file') {
                          return (
                            <td key={idx} className="text-center cursor-pointer">
                              {val[value?.name] ? (
                                <div className="d-flex gap-2">
                                  <Icon.PlayCircle
                                    size={16}
                                    onClick={() =>
                                      setAudioDetails({
                                        ...val,
                                        src: `${config.BASE_URL}cdrs_report/download?filename=${val.recording_file}`
                                      })
                                    }
                                    className="cursor-pointer text-info"
                                  />
                                  <Icon.Download
                                    size={16}
                                    onClick={() => {
                                      fileDownload(
                                        `${config.BASE_URL}cdrs_report/download?filename=${val.recording_file}`
                                      );
                                    }}
                                  />
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                          );
                        }
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={reportData?.total}
              limit={reportData?.limit}
              page={reportData?.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
              }}
              onPageClick={(val) => setFieldValue('page', val)}
              lastPageClick={(val) => setFieldValue('page', val)}
              firstPageClick={(val) => setFieldValue('page', val)}
              onRefreshClick={() => onSubmit()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      {audioDetails && (
        <AudioPlayerModal
          modalOpen={!!audioDetails}
          handleClose={() => setAudioDetails(null)}
          details={audioDetails}
        />
      )}
    </div>
  );
};

export default DidDumpReport;
